/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import GenericCard from "@ui-components/Card/GenericCard";
import ListPageLayout from "@ui-components/ListPage/ListPageLayout";
import ListPageLoading from "@ui-components/ListPage/ListPageLoading";
import ListPageToolbar from "@ui-components/ListPage/ListPageToolbar";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import {
  GenericTable,
  GenericTableColumn,
} from "@ui-components/Table/GenericTable";
import { useColumnVisibility } from "@ui-components/Table/useColumnVisibility";
import { ColumnVisibilityControl } from "@ui-components/Table/ColumnVisibilityControl";
import { isDemoClient } from "@shared/utils/clientEnv";
import { viewHref } from "@shared/navigation/tabs/ViewsProvider";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  FilterField,
  labelId,
  ParsedFilter as OldParsed,
  parseToken,
} from "kontrolEdge/helpers/advancedFilters";
import { useNavigate } from "react-router-dom";
import {
  Fwaas,
  fwaasColumns as baseColumns,
  fwaasColumns,
  shapeFwaasData,
} from "./FwaasData";
import { useAppSelector } from "@redux/hooks";
import { makeFwaasFields } from "./FwaasFieldSpec";
import GenericButton from "@ui-components/Button/GenericButton";
import { fetchFortimanager, fetchFwaasList } from "./request/request";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { useCachedFetch } from "@hooks/useCachedFetch";
import { RefreshingIndicator } from "@shared/components/ListLoading";
import EmptyItems from "@shared/components/EmptyItems";
import ErrorFetchingItems from "@shared/components/ErrorFetching";
import { ConnectionsCell } from "./ConnectionCell";
import AllConnectionsModal from "./AllConectionsModal";
import CredentialsModal from "./CredentialsModal";
import {
  buildConnItemPredicate,
  canonicalStatus,
  connectionIndex,
  connectionKeyMatches,
  ConnectionType,
  parseConnToken,
  RENDER_ORDER,
  statusLabel,
  connectionsSummary,
} from "./ConnectionsHelpers";
import { buildDetailPath, serviceDetailPath } from "@shared/navigation/tabs/detailRoutes";

type FortiManagerData = string | { accessUrl?: string; url?: string };

type FortiManagerResp = {
  success?: boolean;
  message?: string;
  data?: FortiManagerData;
  url?: string;
};

const NO_FWAAS: Fwaas[] = [];

export default function FwaasPage() {
  const { tenantId, partnerId } = useAppSelector((state) => state.selection);
  const isViewingAllCustomers = tenantId === partnerId;
  const api = useAxiosAuth0();

  // Cached per tenant: coming back to the list paints the last answer at once and
  // refreshes it underneath. The key carries the query flags the request sends.
  const {
    data: fwaasList = NO_FWAAS,
    error: fetchError,
    isLoading: isLoadingList,
    isRefreshing: isRefreshingList,
  } = useCachedFetch(
    tenantId ? `kontroledge/fwaas?tenantId=${tenantId}&includeChildServices=true&includeFWInt=true` : null,
    async () => {
      const { status, data } = await fetchFwaasList(api, tenantId);
      if (status >= 500 || data.success === false) {
        throw new Error(data.message || "Unknown server error");
      }
      return Array.isArray(data.data) ? shapeFwaasData(data.data) : [];
    }
  );
  const [fmUrl, setFmUrl] = useState<string | null>(null);
  const [isFetchingFm, setIsFetchingFm] = useState(false);
  const [connectionsModalRow, setConnectionsModalRow] = useState<Fwaas | null>(
    null
  );
  const [isCredentialsOpen, setIsCredentialsOpen] = useState(false);
  const [credentialsModalRow, setCredentialsModalRow] = useState<Fwaas | null>(
    null
  );
  const [activeFilters, setActiveFilters] = useState<OldParsed[]>([]);
  const navigate = useNavigate();

  const fwaasFields = useMemo(
    () => makeFwaasFields(isViewingAllCustomers),
    [isViewingAllCustomers]
  );

  const keyMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of fwaasFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      const label = f.label.toLowerCase();
      m.set(id, id);
      m.set(label, id);
    }
    return m;
  }, [fwaasFields]);

  const fieldById = useMemo(() => {
    const m = new Map<string, FilterField<Fwaas>>();
    for (const f of fwaasFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      m.set(id, f);
    }
    return m;
  }, [fwaasFields]);

  const suggestedKeys = useMemo(() => {
    const headers = fwaasColumns
      .map((c) => c.header)
      .filter(Boolean) as string[];

    if (isViewingAllCustomers && !headers.includes("Customer")) {
      headers.splice(1, 0, "Customer");
    }

    // Ensure our custom keys show up in the searchbar suggestions
    const extra = ["Service ID", "Status", "Connection"];
    for (const k of extra) {
      if (!headers.includes(k)) headers.push(k);
    }
    return headers;
  }, [fwaasColumns, isViewingAllCustomers]);

  const normalizedFilters = useMemo(() => {
    return activeFilters.map((f) => {
      // If searchbar provided an op (live typing), keep it
      const token = f.op
        ? { ...f }
        : parseToken(f.key ? `${f.key}=${f.value}` : f.value);

      // Map key to canonical id
      if (token.key) {
        const k = token.key.trim().toLowerCase();
        token.key = keyMap.get(k) ?? labelId(token.key);
      }

      // If this is a STRING field and user didn't explicitly ask for exact,
      const field = token.key ? fieldById.get(token.key) : undefined;
      if (field?.type === "string") {
        const quoted = token.value.match(/^"(.*)"$/);
        if (quoted) {
          token.value = quoted[1]; // strip quotes
          token.op = "=";
        } else {
          token.op = ":"; // default to contains
        }
      }
      return token;
    });
  }, [activeFilters, keyMap, fieldById]);

  type StringOp = ":" | "=";

  const predicate = useMemo(() => {
    const filters = normalizedFilters;
    const norm = (x: unknown) => String(x ?? "").toLowerCase();
    const matchOp = (hay: string, needle: string, op: StringOp = ":") =>
      !needle ? true : op === "=" ? hay === needle : hay.includes(needle);
    const toStringOp = (o?: ":" | "=" | ">" | "<" | ">=" | "<="): StringOp =>
      o === "=" ? "=" : ":";

    const serviceKeyMatches = (k: string) => {
      const kk = k.replace(/\s+/g, "").toLowerCase();
      return kk === "serviceid" || kk === "service-id";
    };

    const connTokens = filters
    .filter((f) => f.key && connectionKeyMatches(f.key))
    .map((f) => parseConnToken(String(f.value ?? ""), toStringOp(f.op)));
    const connItemPredicate = buildConnItemPredicate(connTokens);
    const hasConnFilter = Boolean(connItemPredicate);

    return (row: Fwaas) => {
      if (!filters.length) return true;

      const rowServiceId = norm(row.serviceId);
      const rowStatusCanon = canonicalStatus(row.status);            // "active" | "down" | "unknown"
      const rowStatusLabel = statusLabel(row.status).toLowerCase();
      const connIdx = connectionIndex(row);

      const custName = norm(row.customer?.name);
      const custId   = norm(row.customer?.chargebeeCustomerId);

      return filters.every((f) => {
        const key = (f.key || "").trim().toLowerCase();
        const valueRaw = String(f.value ?? "");
        const value = valueRaw.toLowerCase();
        const sop = toStringOp(f.op);

        if (!key) {
          return (
            matchOp(rowServiceId, value, ":") ||
            matchOp(rowStatusLabel, value, ":") ||
            matchOp(rowStatusCanon, value, ":") ||
            matchOp(connIdx, value, ":") ||
            matchOp(custName, value, ":") ||
            matchOp(custId, value, ":")          
          );
        }

        if (serviceKeyMatches(key)) {
          return matchOp(rowServiceId, value, sop);
        }

        if (key === "status") {
          if (sop === "=") {
            // Exact: compare canonical values (so Status="Active" matches)
            return canonicalStatus(valueRaw) === rowStatusCanon;
          }
          // Contains: allow label or canonical partials, e.g. "act", "down"
          return matchOp(rowStatusLabel, value, ":") || matchOp(rowStatusCanon, value, ":");
        }

        if (connectionKeyMatches(key)) {
          const tokenMatcher = parseConnToken(valueRaw, sop);
          const perTokenFn = buildConnItemPredicate([tokenMatcher]);
          const b = row.connections ?? {};
          for (const t of RENDER_ORDER) {
            const list = (b as any)[t] as { id: string; subscriptionId: string }[] | undefined;
            if (!list?.length) continue;
            if (perTokenFn && list.some((it) => perTokenFn(t, it))) return true;
          }
          return false;
        }

        if (key === "customer") {
          return (
            matchOp(custName, value, sop) ||
            matchOp(custId, value, sop)
          );
        }

        return false;
      });
    };
  }, [normalizedFilters]);

  const connItemFilterFn = useMemo(() => {
    const filters = normalizedFilters;
    const connTokens = filters
      .filter((f) => f.key && connectionKeyMatches(f.key))
      .map((f) => parseConnToken(String(f.value ?? ""), f.op === "=" ? "=" : ":"));
    return buildConnItemPredicate(connTokens); // may be undefined if no conn filters
  }, [normalizedFilters]);


  const handleConnectionClick = useCallback(
    (type: ConnectionType, idFromCell: string, row: Fwaas) => {
      // idFromCell is the nested connections[*].id (string). Fallback just in case:
      const id = idFromCell ?? "";
      navigate(serviceDetailPath(type, id), { state: { from: "fwaas", row } });
    },
    [navigate]
  );

  const filteredData = useMemo(
    () => fwaasList.filter(predicate),
    [fwaasList, predicate]
  );

  const redirectTo = useMemo(
    () => new URL("/kontrol", window.location.origin).toString(),
    []
  );

  //Fetch Fortimanager on mount
  const didInitFm = useRef(false);
  useEffect(() => {
    const ac = new AbortController();
    if (didInitFm.current) return;
    didInitFm.current = true;
    let cancelled = false;
    (async () => {
      try {
        setIsFetchingFm(true);
        const res = await fetchFortimanager(api, {
          tenantId,
          redirectTo,
          signal: ac.signal,
        });
        const payload: FortiManagerResp = res?.data ?? {};
        // Narrow `payload.data` first
        const d = payload?.data;
        let candidate: string | null = null;

        if (typeof d === "string") {
          candidate = d;
        } else if (d && typeof d === "object") {
          candidate = d.accessUrl ?? d.url ?? null;
        } else {
          candidate = payload?.url ?? null;
        }

        if (!cancelled) setFmUrl(candidate);
      } catch (e) {
        if (!cancelled) setFmUrl(null);
        console.error("fetchFortimanager failed:", e);
      } finally {
        if (!cancelled) setIsFetchingFm(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const columns = useMemo<GenericTableColumn<Fwaas>[]>(() => {
    const cols = [...baseColumns];
    if (isViewingAllCustomers) {
      cols.splice(0, 0, {
        header: "Customer",
        accessor: "customer",
        sortAccessor: (row: Fwaas) => row.customer?.name ?? "",
        renderCell: (row: Fwaas) =>
          row.customer ? (
            <div className="flex flex-col">
              <span className="font-medium">{row.customer.name}</span>
              <span className="text-xs text-gray-500">
                ID: {row.customer.chargebeeCustomerId}
              </span>
            </div>
          ) : (
            "—"
          ),
      });
    }

    return cols.map((col) => {
      if (col.header === "Service ID") {
        return {
          ...col,
          renderCell: (row: Fwaas) => (
            <button
              {...viewHref(buildDetailPath("fwaas", { fwaasId: row.id }))}
              onClick={() => navigate(buildDetailPath("fwaas", { fwaasId: row.id }))}
              className="
              hover:text-semantic-text-brand 
              hover:underline
              focus:underline 
              active:underline
              font-medium
              underline
              text-semantic-text-brand
              "
            >
              {row.serviceId}
            </button>
          ),
        };
      }
      if (col.header === "Connection") {
        return {
          ...col,
          accessor: "connections",
          sortAccessor: (row: Fwaas) => row.connections?.raw ?? "",
          renderCell: (row: Fwaas) => (
            <ConnectionsCell
              row={row}
              onItemClick={handleConnectionClick}
              onShowAll={openConnectionsModal}
              full={true}
              showCard={true}
              scrollMaxHeight={140}
              cardVariant="primary"
              cardShadow="sm"
              filterFn={connItemFilterFn}
            />
          ),
        };
      }

      return col;
    });
  }, [baseColumns, isViewingAllCustomers, connItemFilterFn, handleConnectionClick, navigate]);

  const isDemo = isDemoClient();
  const fwaasColumnVisibility = useColumnVisibility(
    "kontrol:fwaas:columns",
    columns,
    ["Service ID"],
  );
  const tableColumns = isDemo ? fwaasColumnVisibility.visibleColumns : columns;

  const openCredentialsModal = useCallback((row?: Fwaas) => {
    setCredentialsModalRow(row ?? null);
    setIsCredentialsOpen(true);
  }, []);

  const closeCredentialsModal = useCallback(() => {
    setIsCredentialsOpen(false);
    setCredentialsModalRow(null);
  }, []);

  const openConnectionsModal = useCallback((row: Fwaas) => {
    setConnectionsModalRow(row);
  }, []);

  const closeConnectionsModal = useCallback(() => {
    setConnectionsModalRow(null);
  }, []);

  if (isLoadingList) {
    return <ListPageLoading columns={4} />;
  }
  if (fetchError) {
    return <ErrorFetchingItems text={fetchError} />;
  }
  if (fwaasList.length === 0) {
    return <EmptyItems />;
  }

  return (
    <ListPageLayout>
      <RefreshingIndicator active={isRefreshingList} />
      <div className="relative flex-1 min-h-0">
        <GenericCard
          isCollapsible={false}
          className="h-full flex flex-col"
          bodyContent={
            <div className="flex flex-col h-full min-h-0">
              <ListPageToolbar className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex w-full items-center justify-between gap-3">
                  <div className="w-full max-w-full sm:max-w-[500px]">
                    <GenericSearchbar
                      placeholder="Find by attribute or value"
                      suggestedKeys={suggestedKeys}
                      onFiltersChange={setActiveFilters}
                    />
                  </div>
                  {isDemo && (
                    <ColumnVisibilityControl
                      columns={columns}
                      isVisible={fwaasColumnVisibility.isVisible}
                      toggle={fwaasColumnVisibility.toggle}
                      requiredIds={["Service ID"]}
                    />
                  )}
                </div>
                <GenericButton
                  size="md"
                  variant="brand"
                  onClick={() => openCredentialsModal()}
                  label="Management"
                />
              </ListPageToolbar>
              <div className="flex-1 min-h-0 flex flex-col">
              <GenericTable
                columns={tableColumns}
                // On a phone, two lines: ID and status, then customer, name and
                // connections in a few words; the full list is in the detail (CTX-1340).
                mobileCards={{
                  titleColumn: "Service ID",
                  badgeColumn: "Status",
                  omitColumns: ["Connection"],
                  summary: (row) => [
                    row.customer?.name,
                    row.name && row.name !== row.serviceId ? row.name : null,
                    connectionsSummary(row),
                  ],
                  opensFromTitle: true,
                  sortControl: true,
                  itemNoun: "firewall",
                }}
                data={filteredData}
                initialRowsPerPage={10}
                rowsPerPageOptions={[10, 20, 30]}
                width="100%"
                tableClassName="bg-semantic-bg-primary"
                maxTableBodyHeight="100%"
              />
              </div>
            </div>
          }
        />
      </div>
      <CredentialsModal
        isOpen={isCredentialsOpen}
        row={credentialsModalRow}
        onClose={closeCredentialsModal}
        fmUrl={fmUrl}
        isFetchingFm={isFetchingFm}
        api={api}
        tenantId={tenantId}
      />
      <AllConnectionsModal
        isOpen={!!connectionsModalRow}
        row={connectionsModalRow}
        onClose={closeConnectionsModal}
        onItemClick={handleConnectionClick}
      />
    </ListPageLayout>
  );
}
